import { FC, useEffect } from 'react';
import { Stack } from '@mui/material';
import { FormHandle } from '@/hooks/use-form-handle.hook';
import { FormikValues } from 'formik';
import InputControl from '@/components/form/input-control';
import IntercomButton from '@/components/intercom/intercom-button';
import InlineToast from '@/components/toasts/inline';
import { RegexClassifierModel } from '@/lib/models/classifier';
import {
  REGEX_ANCHOR_WARNING,
  REGEX_PATTERN_MAX_LENGTH,
  patternUsesTextAnchors,
} from '@/lib/regex-helpers';
import MatchPreview from './match-preview';
import { ReadonlySection } from '@/components/form/readonly-view/section';
import Text from '@/components/text';

interface Props {
  formHandle: FormHandle<FormikValues>;
  readonly: boolean;
  initialTestPatterns?: string;
}

const CustomEntityForm: FC<Props> = ({ formHandle, readonly, initialTestPatterns }) => {
  const { values, setFieldValue } = formHandle;
  const { config } = values as RegexClassifierModel;
  const { pattern: regexPattern } = config;
  const showAnchorWarning = patternUsesTextAnchors(regexPattern);

  // manually update a field value for templates so the form is marked as dirty and can be submitted
  useEffect(() => {
    setFieldValue('enabled', true);
  }, [setFieldValue]);

  return (
    <Stack gap={1}>
      <InputControl
        type="text"
        name="name"
        label="Name"
        formHandle={formHandle}
        readonly={readonly}
        autoFocus
      />
      <InputControl
        type="textarea"
        name="description"
        label="Description"
        readonly={readonly}
        formHandle={formHandle}
        rows={3}
      />
      <InputControl
        type="regex"
        name="config.pattern"
        label="Regex Pattern"
        formHandle={formHandle}
        readonly={readonly}
        maxLength={REGEX_PATTERN_MAX_LENGTH}
      />
      {showAnchorWarning && <InlineToast level="warning" message={REGEX_ANCHOR_WARNING} />}
      <InputControl
        name="enabled"
        label="Enabled"
        type="switch"
        formHandle={formHandle}
        readonly={readonly}
        fullWidth={false}
      />
      <ReadonlySection label="Custom Entity Tester" />
      <Text size="small" color="grey">
        Write your example value inside a full sentence, the way a user would type it in a prompt.
        Patterns are matched against the whole prompt, not a value on its own.
      </Text>
      <IntercomButton
        intercomArticle="custom-entity-regex"
        label="Learn about defining Custom Entities"
        flatButton
      />
      <MatchPreview regexPattern={regexPattern} initialTestPatterns={initialTestPatterns} />
    </Stack>
  );
};

export default CustomEntityForm;
